import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Clover, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const { login, language } = useAuth();
  const navigate = useNavigate();
  const t = useTranslation(language);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password, requires2FA ? totpCode : null);
      toast.success(t('loginSuccess'));
      navigate('/dashboard');
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || t('error');
      if (status === 403 && detail === '2fa_required') {
        setRequires2FA(true);
      } else if (status === 403) {
        // Email not verified — show clear message with link to verify
        toast.error(detail, {
          duration: 6000,
          action: {
            label: 'Verificar ahora',
            onClick: () => navigate('/verify-email', { state: { email } }),
          },
        });
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 xl:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-emerald-900 rounded-sm flex items-center justify-center">
              <Clover className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-display font-bold text-xl tracking-tight text-slate-900">
              Trebol Finance by Leapifyes
            </span>
          </div>

          {/* Header */}
          <div className="mb-10">
            <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight mb-2">
              {t('welcome')}
            </h1>
            <p className="text-slate-500 text-sm">
              {t('tagline')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {!requires2FA ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {t('email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="nombre@ejemplo.com"
                    className="border-0 border-b border-slate-200 rounded-none px-0 py-3 text-slate-900 focus:ring-0 focus:border-emerald-900 transition-colors placeholder:text-slate-300"
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                    {t('password')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="border-0 border-b border-slate-200 rounded-none px-0 py-3 pr-10 text-slate-900 focus:ring-0 focus:border-emerald-900 transition-colors placeholder:text-slate-300"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-0 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="text-right mt-2">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-slate-500 hover:text-emerald-900 transition-colors"
                      data-testid="forgot-password-link"
                    >
                      {t('forgotPassword')}
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-600 mb-4">
                  Tu cuenta tiene verificación en dos pasos activa. Introduce el código de tu aplicación autenticadora.
                </p>
                <Label htmlFor="totp" className="text-xs uppercase tracking-widest font-semibold text-slate-500">
                  Código de verificación
                </Label>
                <Input
                  id="totp"
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\s/g, ''))}
                  placeholder="000000"
                  maxLength={8}
                  className="border-0 border-b border-slate-200 rounded-none px-0 py-3 text-slate-900 focus:ring-0 focus:border-emerald-900 transition-colors placeholder:text-slate-300 tracking-widest text-center text-xl"
                  required
                  autoFocus
                  data-testid="login-totp-input"
                />
                <button
                  type="button"
                  onClick={() => { setRequires2FA(false); setTotpCode(''); }}
                  className="text-xs text-slate-500 hover:text-emerald-900 mt-2 transition-colors"
                >
                  ← Volver
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-sm uppercase tracking-widest text-xs font-bold py-6 transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? t('loading') : t('login')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          {/* Register link */}
          <p className="mt-8 text-center text-sm text-slate-500">
            {t('noAccount')}{' '}
            <Link
              to="/register"
              className="text-emerald-900 font-semibold hover:underline"
              data-testid="register-link"
            >
              {t('register')}
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div
        className="hidden lg:block lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-emerald-900/80" />
        <div className="absolute inset-0 flex flex-col justify-end p-12">
          <blockquote className="text-white/90 text-lg font-display leading-relaxed max-w-md">
            "Ningún euro entra o sale sin quedar registrado, categorizado, analizado y comprendido."
          </blockquote>
          <p className="text-white/60 text-sm mt-4 uppercase tracking-widest">
            Principio Fundamental
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
